import React, { useState } from "react";
import { Button, Input, Select, Table, Popconfirm } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  CreateAdminChargeParams,
  UpdateAdminChargeParams,
} from "~/services/admin-charge/admin-charge.types";
import { NumberInput } from "../components/number_input";
import {
  useGetAdminCharges,
  useCreateAdminCharge,
  useUpdateAdminCharge,
  useDeleteAdminCharge,
} from "~/services/admin-charge";
import { AdminChargeUnit } from "~/utils/constants";
import { useNotification } from "~/hooks";
import { TAdminCharge } from "~/types/admin-charge";

interface AdminTabProps {
  onAdminChargesChange?: (charges: TAdminCharge[]) => void;
}

export const AdminTab: React.FC<AdminTabProps> = ({ onAdminChargesChange }) => {
  const [params, setParams] = useState({ page: 1, pageSize: 10 });
  const [newChargeName, setNewChargeName] = useState("");
  const [newChargeRate, setNewChargeRate] = useState(0);
  const [newChargeUnit, setNewChargeUnit] = useState<AdminChargeUnit>(
    AdminChargeUnit.EACH
  );
  const [modifiedCharges, setModifiedCharges] = useState<{
    [id: string]: Partial<TAdminCharge>;
  }>({});
  const notification = useNotification();

  const { data, isLoading, error } = useGetAdminCharges({
    params,
    query: { staleTime: 1000 },
  });

  const createMutation = useCreateAdminCharge();
  const updateMutation = useUpdateAdminCharge();
  const deleteMutation = useDeleteAdminCharge();

  const hasChanges = (id: string): boolean => {
    const modified = modifiedCharges[id];
    if (!modified) return false;
    const original = data?.items.find((c) => c.id === id);
    if (!original) return true;
    return (
      (modified.rate !== undefined && modified.rate !== original.rate) ||
      (modified.unit !== undefined && modified.unit !== original.unit)
    );
  };

  const handleChargeChange = (
    id: string,
    updates: Partial<Omit<TAdminCharge, "name">>
  ) => {
    setModifiedCharges((prev) => {
      const existingCharge = prev[id] || {};
      return {
        ...prev,
        [id]: {
          ...existingCharge,
          ...updates,
        },
      };
    });

    if (onAdminChargesChange) {
      const updatedCharges = (data?.items || []).map((c) =>
        c.id === id ? { ...c, ...modifiedCharges[id], ...updates } : c
      );
      onAdminChargesChange(updatedCharges);
    }
  };

  const handleSave = (record: TAdminCharge) => {
    const modifiedCharge = modifiedCharges[record.id];
    if (!modifiedCharge) return;

    const updateData: UpdateAdminChargeParams = {
      name: record.name, // Preserve original name
      rate: modifiedCharge.rate ?? record.rate,
      unit: modifiedCharge.unit ?? record.unit,
    };

    updateMutation.mutate(
      { id: record.id, data: updateData },
      {
        onSuccess: () => {
          notification.success({
            message: "Charge updated",
            description: "Admin charge was successfully updated.",
          });
          setModifiedCharges((prev) => {
            const newModified = { ...prev };
            delete newModified[record.id];
            return newModified;
          });
        },
        onError: (err: Error) => {
          notification.error({
            message: "Update failed",
            description: err.message || "Failed to update admin charge.",
          });
        },
      }
    );
  };

  const deleteCharge = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        notification.success({
          message: "Charge deleted",
          description: "Admin charge was successfully deleted.",
        });
        setModifiedCharges((prev) => {
          const newModified = { ...prev };
          delete newModified[id];
          return newModified;
        });
      },
      onError: (err: Error) => {
        notification.error({
          message: "Delete failed",
          description: err.message || "Failed to delete admin charge.",
        });
      },
    });
  };

  const addNewCharge = () => {
    if (!newChargeName.trim()) {
      notification.warning({
        message: "Invalid input",
        description: "Please enter a charge name.",
      });
      return;
    }

    const newCharge: CreateAdminChargeParams = {
      name: newChargeName,
      rate: newChargeRate,
      unit: newChargeUnit,
    };

    createMutation.mutate(newCharge, {
      onSuccess: () => {
        notification.success({
          message: "Charge created",
          description: "Admin charge was successfully created.",
        });
        setNewChargeName("");
        setNewChargeRate(0);
        setNewChargeUnit(AdminChargeUnit.EACH);
      },
      onError: (err: Error) => {
        notification.error({
          message: "Creation failed",
          description: err.message || "Failed to create admin charge.",
        });
      },
    });
  };

  const columns: ColumnsType<TAdminCharge> = [
    {
      title: "Admin Charge",
      dataIndex: "name",
      key: "name",
      render: (value: string) => <span>{value}</span>,
    },
    {
      title: "Rate ($)",
      dataIndex: "rate",
      key: "rate",
      render: (value: number | undefined, record: TAdminCharge) => (
        <NumberInput
          value={modifiedCharges[record.id]?.rate ?? value ?? 0}
          prefix="$"
          onChange={(newValue) =>
            handleChargeChange(record.id, { rate: newValue })
          }
        />
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (value: AdminChargeUnit, record: TAdminCharge) => (
        <Select
          value={modifiedCharges[record.id]?.unit ?? value}
          onChange={(newValue: AdminChargeUnit) =>
            handleChargeChange(record.id, { unit: newValue })
          }
          className="w-full"
        >
          <Select.Option value={AdminChargeUnit.EACH}>Each</Select.Option>
          <Select.Option value={AdminChargeUnit.HOURLY}>Hourly</Select.Option>
        </Select>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: TAdminCharge) => (
        <div className="flex space-x-2">
          <Button
            type="primary"
            size="small"
            loading={
              updateMutation.isPending &&
              updateMutation.variables?.id === record.id
            }
            onClick={() => handleSave(record)}
            disabled={!hasChanges(record.id)}
          >
            Save
          </Button>
          <Popconfirm
            title="Delete Admin Charge"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => deleteCharge(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ loading: deleteMutation.isPending }}
          >
            <Button
              danger
              size="small"
              loading={
                deleteMutation.isPending &&
                deleteMutation.variables === record.id
              }
            >
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setParams({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  if (error) {
    notification.error({
      message: "Loading failed",
      description: "Failed to load admin charges.",
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Admin Charges</h3>
        <p className="text-gray-500 mb-4">
          Configure rates for administrative services
        </p>
      </div>

      <Table
        columns={columns}
        dataSource={data?.items || []}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: params.page,
          pageSize: params.pageSize,
          total: data?.meta.totalItems || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        className="bg-white"
      />

      <div className="p-4 border rounded-lg bg-gray-50">
        <h4 className="font-medium mb-3">Add New Admin Charge</h4>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Input
            placeholder="Charge name"
            className="col-span-2"
            value={newChargeName}
            onChange={(e) => setNewChargeName(e.target.value)}
          />
          <NumberInput
            value={newChargeRate}
            prefix="$"
            onChange={(value) => setNewChargeRate(value)}
          />
          <Select
            value={newChargeUnit}
            onChange={(value: AdminChargeUnit) => setNewChargeUnit(value)}
            className="w-full"
          >
            <Select.Option value={AdminChargeUnit.EACH}>Each</Select.Option>
            <Select.Option value={AdminChargeUnit.HOURLY}>Hourly</Select.Option>
          </Select>
        </div>
        <div className="flex justify-end">
          <Button
            type="primary"
            size="small"
            loading={createMutation.isPending}
            onClick={addNewCharge}
            disabled={!newChargeName.trim()}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
